import { useEffect, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { Modal } from '@bloomays-lib/ui.shared';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTalentBoardEvents from '../../hooks/useTalentBoardEvents';
import EventsKpis from './EventsKpis';
import EventsGrid from './EventsGrid';

type EventsModalProps = {
  jobId: string;
  open: boolean;
  onClose: () => void;
};

const EventsModal = ({ jobId, open, onClose }: EventsModalProps): ReactNode => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { events, kpis, loading, fetchEvents, fetchKpis } = useTalentBoardEvents();

  useEffect(() => {
    if (open) {
      void fetchEvents({ variables: { jobId } });
      void fetchKpis({ variables: { jobId } });
    }
  }, [open, jobId, fetchEvents, fetchKpis]);

  useEffect(() => {
    if (isMobile && open) {
      onClose();
    }
  }, [isMobile, open, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      additionnalCSS={{
        padding: '0px',
        width: 'calc(100vw - 200px)',
        height: '100vh',
        right: '0',
        top: '0',
        left: 'auto',
        transform: 'none',
        [theme.breakpoints.down('lg')]: {
          width: '90vw',
        },
        [theme.breakpoints.down('md')]: {
          backgroundColor: theme.palette.neutral[10],
          overflowY: 'auto',
          width: '100vw',
        },
      }}
      additionnalCSSCross={{
        float: 'left',
        position: 'absolute',
        top: '24px',
        left: '24px',
        zIndex: '1000',
        [theme.breakpoints.down('md')]: {
          top: '30px',
          left: '12px',
        },
      }}
    >
      <Container>
        <SideModal>
          <EventsKpis kpis={kpis} loading={loading} />
        </SideModal>
        <Content>
          <EventsGrid events={events} loading={loading} />
        </Content>
      </Container>
    </Modal>
  );
};

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  height: '100%',
  backgroundColor: theme.palette.neutral.white,
  [theme.breakpoints.down('md')]: {
    backgroundColor: theme.palette.neutral[10],
    position: 'unset',
    flexDirection: 'column',
  },
}));

const SideModal = styled('div')(({ theme }) => ({
  padding: '60px 24px 0',
  minWidth: '280px',
  maxWidth: '280px',
  backgroundColor: theme.palette.neutral[10],
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    marginTop: '0px',
    minWidth: 'unset',
    maxWidth: 'unset',
    overflowY: 'unset',
  },
}));

const Content = styled('div')(({ theme }) => ({
  width: '100%',
  padding: '24px',
  backgroundColor: theme.palette.red[20],
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

export default EventsModal;
